import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as subscriptionActions from './subscription.actions';
import * as informativePageActions from '../informative-page/informative-page.actions';
import * as moduleActions from '../module/module.actions';
import * as moduleItemActions from '../module-item/module-item.actions';
import * as moment from 'moment';
import { HttpErrorResponse } from '@angular/common/http';
import { CustomRouterNavigatedAction } from 'src/app/core/custom-route-serializer';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state';
import { ToastrService } from 'ngx-toastr';
import { SubscriptionService } from '../../services/subscription/subscription.service';
import { DashboardCamelCasePipe } from 'src/app/core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { DashboardSnakeCasePipe } from 'src/app/core/pipes/dashboard-snake-case/dashboard-snake-case.pipe';
import { STUDIES_ROUTES } from '../../studies/studies.routes';
import { changeDateFormat } from '../../studies/helpers/date-helper';

@Injectable()
export class SubscriptionEffects {
  subscriptionRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter((route: CustomRouterNavigatedAction) => {
        return route.payload.routerState.url.endsWith(`/${STUDIES_ROUTES.SUBSCRIPTION}`);
      }),
      switchMap((action) => {
        return [
          subscriptionActions.getSubscription({
            studyId: action.payload.routerState.params.id,
          }),
          moduleItemActions.getModuleItemsConfig(),
        ];
      })
    )
  );

  getSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(subscriptionActions.getSubscription),
      switchMap(({ studyId }) => {
        return this.subscriptionService.getSubscription(studyId).pipe(
          map((subscription) => {
            const subscriptionCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(subscription);
            if (subscriptionCamelCase.endsAt) {
              const endsDate = new Date(changeDateFormat(subscriptionCamelCase.endsAt));
              subscriptionCamelCase.endsAt = moment(endsDate);
            }
            return subscriptionActions.getSubscriptionSuccess({
              fetchedSubscription: subscriptionCamelCase,
            });
          }),
          catchError((err: HttpErrorResponse) => of(subscriptionActions.getSubscriptionError({ err })))
        );
      })
    )
  );

  getSubscriptionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(subscriptionActions.getSubscriptionSuccess),
      switchMap(({ fetchedSubscription }) => {
        return [
          informativePageActions.getInformativePagesSuccess({
            informativePages: fetchedSubscription.pages,
          }),
          moduleActions.setSelectedModule({
            selectedModule: fetchedSubscription?.module,
          }),
          moduleItemActions.setSelectedModuleItems({
            moduleItems: fetchedSubscription?.module?.moduleItems,
          }),
        ];
      })
    )
  );

  getSubscriptionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(subscriptionActions.getSubscriptionError),
        tap(({ err }) => this.toastrService.error(`Error in fetching subscription details ${err.error}`))
      ),
    { dispatch: false }
  );

  updateSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(subscriptionActions.updateSubscription),
      switchMap(({ subscription }) => {
        const subscriptionSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(subscription);
        return this.subscriptionService.updateSubscription(subscriptionSnakeCase).pipe(
          map((newSubscription) => {
            const newSubscriptionCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(newSubscription);
            return subscriptionActions.updateSubscriptionSuccess({
              updatedSubscription: newSubscriptionCamelCase,
            });
          }),
          catchError((err: HttpErrorResponse) => of(subscriptionActions.updateSubscriptionError({ err })))
        );
      })
    )
  );

  updateSubscriptionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(subscriptionActions.updateSubscriptionSuccess),
        tap(({ updatedSubscription }) => {
          this.toastrService.success('Subscription page saved');
        })
      ),
    { dispatch: false }
  );

  updateSubscriptionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(subscriptionActions.updateSubscriptionError),
        tap(({ err }) => this.toastrService.error(`Unable to save subscription page ${err.error}`))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private subscriptionService: SubscriptionService,
    private store: Store<AppState>,
    private toastrService: ToastrService,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private dashboardSnakeCasePipe: DashboardSnakeCasePipe
  ) {}
}
